// src/pages/maintenance/MaintenanceAssetDetailPage.js
// Single asset view: info, QR code, meters (with manual reading entry), linked documents, and
// recent work orders. See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import maintenanceService from '../../services/maintenanceService';
import tempMonitoringService from '../../services/tempMonitoringService';
import ConfirmModal from '../../components/ConfirmModal';
import SearchableSelect from '../../components/SearchableSelect';
import MaintenanceNav from '../../components/MaintenanceNav';
import AssetModal from './AssetModal';
import QuickMaintenanceStatusModal from './QuickMaintenanceStatusModal';
import ManualTab from './ManualTab';
import KnowledgeChatTab from './KnowledgeChatTab';

const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function AddMeterRow({ assetId, onAdded, onCancel }) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [tempSensorId, setTempSensorId] = useState('');
  const [sensors, setSensors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    tempMonitoringService.getSensors().then(setSensors).catch(() => setSensors([]));
  }, []);

  // Auto-fill unit when linking to a sensor — a linked meter's readings always come from the
  // sensor's tempC poll, so its unit is fixed as °C regardless of what's typed.
  const linkedUnit = tempSensorId ? '°C' : unit;

  const submit = async () => {
    if (!name.trim() || !linkedUnit.trim()) { setError('Name and unit are required.'); return; }
    setSaving(true); setError('');
    try {
      await maintenanceService.createMeter({ assetId, name: name.trim(), unit: linkedUnit.trim(), tempSensorId: tempSensorId || null });
      onAdded();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create meter.');
      setSaving(false);
    }
  };

  return (
    <tr>
      <td colSpan={4}>
        {error && <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{error}</div>}
        <div className="row g-2 align-items-center">
          <div className="col-md-3">
            <input className="form-control form-control-sm" placeholder="Meter name (e.g. Internal Temp)" value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>
          <div className="col-md-2">
            <input className="form-control form-control-sm" placeholder="Unit (e.g. hrs, cycles)" value={linkedUnit} onChange={e => setUnit(e.target.value)} disabled={!!tempSensorId} />
          </div>
          <div className="col-md-4">
            <SearchableSelect
              options={sensors.map(s => ({ value: s.id, label: s.name }))}
              value={tempSensorId}
              onChange={val => setTempSensorId(val || '')}
              placeholder="Link to a temp sensor (optional)"
              size="sm"
            />
          </div>
          <div className="col-md-3 d-flex gap-1">
            <button className="btn btn-sm btn-primary" disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Add Meter'}</button>
            <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

function MeterReadingRow({ meter, onReadingAdded }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (value === '') return;
    setSaving(true);
    try {
      await maintenanceService.addMeterReading(meter.id, { value: parseFloat(value) });
      setValue('');
      onReadingAdded();
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td style={{ fontSize: 13 }}>{meter.name}</td>
      <td style={{ fontSize: 13 }}>{meter.currentValue} {meter.unit}</td>
      <td style={{ fontSize: 12 }} className="text-muted">{meter.lastReadingAt ? new Date(meter.lastReadingAt).toLocaleString() : '—'}</td>
      <td>
        <div className="input-group input-group-sm" style={{ maxWidth: 180 }}>
          <input className="form-control" placeholder="New reading" value={value} onChange={e => setValue(e.target.value)} />
          <button className="btn btn-outline-primary" disabled={saving} onClick={submit}>Log</button>
        </div>
      </td>
    </tr>
  );
}

// Equipment Health Trend Detection — self-baselining signals computed daily, see
// backend/services/assetHealthService.js. `checks` is sorted newest-first by the API.
function TrendStat({ label, current, baseline, suffix, higherIsWorse = true }) {
  if (current == null || baseline == null) return null;
  const delta = Number(current) - Number(baseline);
  const worse = higherIsWorse ? delta > 0 : delta < 0;
  const arrow = delta === 0 ? '→' : (delta > 0 ? '▲' : '▼');
  return (
    <div className="col-6 col-md-4">
      <div className="text-muted" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>
        {Number(current).toFixed(1)}{suffix}
        <span className={`ms-1 ${worse ? 'text-danger' : 'text-success'}`} style={{ fontSize: 12 }}>
          {arrow} {Math.abs(delta).toFixed(1)}{suffix}
        </span>
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>was {Number(baseline).toFixed(1)}{suffix}</div>
    </div>
  );
}

function HealthCard({ checks, assetId, onWorkOrderGenerated }) {
  const latest = checks[0];
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);

  const generateWo = async () => {
    setGenerating(true);
    try {
      const wo = await maintenanceService.generateHealthWorkOrder(assetId);
      onWorkOrderGenerated();
      navigate(`/maintenance/work-orders/${wo.id}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className={`card mb-3 ${latest.flagged ? 'border-danger' : ''}`}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="card-title mb-0">Equipment Health</h6>
          <span className="text-muted" style={{ fontSize: 11 }}>Last checked {new Date(latest.checkedAt).toLocaleString()}</span>
        </div>

        {latest.flagged ? (
          <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>
            <strong>Possible developing issue detected:</strong>
            <ul className="mb-1 mt-1">
              {(latest.flagReasons || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
            {latest.workOrderId ? (
              <Link to={`/maintenance/work-orders/${latest.workOrderId}`} className="btn btn-sm btn-outline-danger mt-1">View Work Order →</Link>
            ) : (
              <button className="btn btn-sm btn-danger mt-1" disabled={generating} onClick={generateWo}>
                {generating ? 'Generating…' : '⚡ Generate Work Order'}
              </button>
            )}
          </div>
        ) : (
          <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>No degrading trends detected — behavior is consistent with its own recent history.</div>
        )}

        <div className="row g-3">
          <TrendStat label="Time in range (7d)" current={latest.timeInRangePct} baseline={latest.timeInRangePctBaseline} suffix="%" higherIsWorse={false} />
          <TrendStat label="Avg. recovery time" current={latest.avgRecoveryMinutes} baseline={latest.avgRecoveryMinutesBaseline} suffix=" min" higherIsWorse />
          <TrendStat label="Avg. temp (7d)" current={latest.avgTempC} baseline={latest.avgTempCBaseline} suffix="°C" higherIsWorse={false} />
        </div>

        {checks.length > 1 && (
          <details className="mt-3">
            <summary className="text-muted" style={{ fontSize: 12, cursor: 'pointer' }}>History ({checks.length} checks)</summary>
            <table className="table table-sm mt-2">
              <thead><tr><th>Date</th><th>Flagged</th><th>Reasons</th></tr></thead>
              <tbody>
                {checks.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontSize: 12 }}>{new Date(c.checkedAt).toLocaleDateString()}</td>
                    <td style={{ fontSize: 12 }}>{c.flagged ? 'Yes' : 'No'}</td>
                    <td style={{ fontSize: 12 }}>{(c.flagReasons || []).join('; ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </div>
    </div>
  );
}

export default function MaintenanceAssetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [locations, setLocations] = useState([]);
  const [recentWorkOrders, setRecentWorkOrders] = useState([]);
  const [healthChecks, setHealthChecks] = useState([]);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showQuickMaintModal, setShowQuickMaintModal] = useState(false);
  const [showReturnConfirm, setShowReturnConfirm] = useState(false);
  const [returning, setReturning] = useState(false);
  const [showAddMeter, setShowAddMeter] = useState(false);
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Batched 2-at-a-time rather than all 5 at once — this page alone firing 4-5 concurrent
      // requests can trip the per-company concurrency throttle (middleware/connectionThrottle.js,
      // default limit 5) alongside the sidebar's own background polling.
      const assetData = await maintenanceService.getAsset(id);
      const [locationList, qr] = await Promise.all([
        maintenanceService.getLocations(),
        maintenanceService.getAssetQr(id).catch(() => null),
      ]);
      const [checks, scanResult] = await Promise.all([
        maintenanceService.getAssetHealthChecks(id).catch(() => []),
        maintenanceService.scanQrCode(assetData.qrCodeKey).catch(() => null),
      ]);
      setAsset(assetData);
      setLocations(locationList);
      setQrDataUrl(qr?.dataUrl || '');
      setHealthChecks(checks);
      setRecentWorkOrders(scanResult?.recentWorkOrders || []);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async () => {
    await maintenanceService.deleteAsset(id);
    navigate('/maintenance/assets');
  };

  const downloadLabel = async () => {
    const blob = await maintenanceService.downloadAssetQrLabel(asset.id);
    downloadBlob(blob, `asset-${asset.id}-qr.pdf`);
  };

  const handleReturnToOperational = async () => {
    setReturning(true);
    try {
      await maintenanceService.updateAsset(id, { status: 'operational' });
      setShowReturnConfirm(false);
      await load();
    } finally {
      setReturning(false);
    }
  };

  const isSuppressing = asset && ['under_maintenance', 'out_of_service'].includes(asset.status);

  if (loading) return <div className="container-fluid py-3 text-center text-muted">Loading…</div>;
  if (!asset) return <div className="container-fluid py-3 text-center text-muted">Asset not found.</div>;

  return (
    <>
      <MaintenanceNav />
      <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <Link to="/maintenance/assets" className="text-muted" style={{ fontSize: 13 }}>&larr; All Assets</Link>
          <h4 className="mb-0 mt-1">{asset.name}</h4>
          <span className="text-muted" style={{ fontSize: 13 }}>{labelize(asset.category)} {asset.serialNumber ? `· S/N ${asset.serialNumber}` : ''}</span>
        </div>
        <div className="d-flex gap-2">
          <Link to={`/maintenance/work-orders?assetId=${asset.id}`} className="btn btn-primary btn-sm">+ New Work Order</Link>
          {isSuppressing ? (
            <button className="btn btn-success btn-sm" onClick={() => setShowReturnConfirm(true)}>✓ Back in Operation</button>
          ) : asset.status !== 'decommissioned' && (
            <button className="btn btn-warning btn-sm" onClick={() => setShowQuickMaintModal(true)}>🔧 Mark Under Maintenance</button>
          )}
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowEditModal(true)}>Edit</button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => setShowArchiveConfirm(true)}>Archive</button>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        {[['overview', 'Overview'], ['manual', 'Manual & PM Generation'], ['ask-ai', 'Ask AI']].map(([k, label]) => (
          <li className="nav-item" key={k}>
            <button className={`nav-link ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{label}</button>
          </li>
        ))}
      </ul>

      {tab === 'manual' && <ManualTab asset={asset} onAssetUpdated={load} />}
      {tab === 'ask-ai' && <KnowledgeChatTab asset={asset} />}

      {tab === 'overview' && (
      <div className="row g-3">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">Details</h6>
              <table className="table table-sm table-borderless mb-0" style={{ fontSize: 13 }}>
                <tbody>
                  <tr><td className="text-muted">Make / Model</td><td>{[asset.make, asset.model].filter(Boolean).join(' / ') || '—'}</td></tr>
                  <tr><td className="text-muted">Location</td><td>{asset.Location?.name || '—'}</td></tr>
                  <tr>
                    <td className="text-muted">Status</td>
                    <td>
                      {labelize(asset.status)}
                      {asset.statusSince && ['under_maintenance', 'out_of_service'].includes(asset.status) && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          since {new Date(asset.statusSince).toLocaleString()}
                          {asset.statusReason && <> — {asset.statusReason}</>}
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr><td className="text-muted">Criticality</td><td>{labelize(asset.criticalityLevel)}</td></tr>
                  <tr><td className="text-muted">Purchase Date</td><td>{asset.purchaseDate || '—'}</td></tr>
                  <tr><td className="text-muted">Warranty Expiry</td><td>{asset.warrantyExpiryDate || '—'}</td></tr>
                  <tr><td className="text-muted">Last Serviced</td><td>{asset.lastServicedAt ? new Date(asset.lastServicedAt).toLocaleDateString() : '—'}</td></tr>
                  <tr><td className="text-muted">Next PM Due</td><td>{asset.nextPmDueAt ? new Date(asset.nextPmDueAt).toLocaleDateString() : '—'}</td></tr>
                </tbody>
              </table>
              {asset.notes && <p className="text-muted mt-2" style={{ fontSize: 13 }}>{asset.notes}</p>}
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-body text-center">
              <h6 className="card-title">QR Code</h6>
              {qrDataUrl && <img src={qrDataUrl} alt="Asset QR" style={{ width: 140, height: 140 }} />}
              <div className="mt-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={downloadLabel}>Download Label</button>
              </div>
            </div>
          </div>

          {asset.documents?.length > 0 && (
            <div className="card mt-3">
              <div className="card-body">
                <h6 className="card-title">Documents</h6>
                <ul className="list-unstyled mb-0" style={{ fontSize: 13 }}>
                  {asset.documents.map(d => (
                    <li key={d.id} className="mb-1">
                      <span className="badge bg-secondary-subtle text-secondary me-2" style={{ fontSize: 10 }}>{labelize(d.documentType)}</span>
                      {d.document?.name || d.document?.fileName}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="col-md-8">
          {healthChecks.length > 0 && <HealthCard checks={healthChecks} assetId={asset.id} onWorkOrderGenerated={load} />}

          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title mb-0">Meters</h6>
                {!showAddMeter && <button className="btn btn-sm btn-outline-primary" onClick={() => setShowAddMeter(true)}>+ Add Meter</button>}
              </div>
              {(asset.Meters?.length > 0 || showAddMeter) && (
                <table className="table table-sm">
                  <thead><tr><th>Meter</th><th>Current</th><th>Last Reading</th><th></th></tr></thead>
                  <tbody>
                    {asset.Meters?.map(m => <MeterReadingRow key={m.id} meter={m} onReadingAdded={load} />)}
                    {showAddMeter && (
                      <AddMeterRow
                        assetId={asset.id}
                        onAdded={() => { setShowAddMeter(false); load(); }}
                        onCancel={() => setShowAddMeter(false)}
                      />
                    )}
                  </tbody>
                </table>
              )}
              {!asset.Meters?.length && !showAddMeter && <p className="text-muted mb-0" style={{ fontSize: 13 }}>No meters configured for this asset.</p>}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h6 className="card-title">Recent Work Orders</h6>
              {recentWorkOrders.length ? (
                <table className="table table-sm table-hover">
                  <thead><tr><th>WO #</th><th>Title</th><th>Type</th><th>Status</th><th>Due</th></tr></thead>
                  <tbody>
                    {recentWorkOrders.map(wo => (
                      <tr key={wo.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/maintenance/work-orders/${wo.id}`)}>
                        <td style={{ fontSize: 13 }}>{wo.woNumber}</td>
                        <td style={{ fontSize: 13 }}>{wo.title}</td>
                        <td style={{ fontSize: 13 }}>{labelize(wo.type)}</td>
                        <td style={{ fontSize: 13 }}>{labelize(wo.status)}</td>
                        <td style={{ fontSize: 13 }}>{wo.dueAt ? new Date(wo.dueAt).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-muted mb-0" style={{ fontSize: 13 }}>No work orders yet for this asset.</p>}
            </div>
          </div>
        </div>
      </div>
      )}

      {showEditModal && (
        <AssetModal asset={asset} locations={locations} onSave={() => { setShowEditModal(false); load(); }} onClose={() => setShowEditModal(false)} />
      )}

      {showQuickMaintModal && (
        <QuickMaintenanceStatusModal
          asset={asset}
          onSave={() => { setShowQuickMaintModal(false); load(); }}
          onClose={() => setShowQuickMaintModal(false)}
        />
      )}

      <ConfirmModal
        show={showArchiveConfirm}
        title="Archive Asset"
        message={`Archive "${asset.name}"? It will be hidden from active lists but kept for historical work orders.`}
        confirmLabel="Archive"
        onConfirm={handleArchive}
        onCancel={() => setShowArchiveConfirm(false)}
      />

      <ConfirmModal
        show={showReturnConfirm}
        title="Back in Operation"
        message={`Mark "${asset.name}" back as Operational? New temperature alarms will resume requiring normal review from now on.`}
        confirmLabel={returning ? 'Saving…' : 'Mark Operational'}
        variant="success"
        onConfirm={handleReturnToOperational}
        onCancel={() => setShowReturnConfirm(false)}
      />
      </div>
    </>
  );
}
